import { experiments } from '@/utils/experiment/manifest';
import { EnrollmentType } from '@/utils/experiment/use-experiment';
import { useExperiment } from '@/utils/experiment/use-experiment';

export type LoomLandOptInCohort = 'control' | 'radio' | 'longText';

/**
 * Read-only cohort accessor for the TWCG-2108 Loom Land opt-in experiment.
 *
 * This hook only reads the arm — it does NOT fire an exposure (the manifest sets
 * `fireExperimentExposure: false`). Exposure is fired manually on account
 * creation from `/try/cloud/signup` (see logged-out-loom.tsx); the `choose`
 * screen just reads the cohort here to render the right arm.
 *
 * `cohort` defaults to `'control'` until the arm resolves, so callers MUST gate
 * cohort-dependent side effects (e.g. redirects) on `isResolved`.
 *
 * `isEnrolled` is separate from `isResolved` so callers can tell apart the
 * transient "enrolled but arm not resolved yet" window (wait, render nothing)
 * from "definitively not in the experiment" — e.g. a visitor who lands on the
 * choose page without Loom intent or while signed out — which should fall through
 * to the standard signup flow now. Both cases have `isResolved === false`. See
 * loom-choice-page.template.tsx.
 */
export const useLoomLandOptIn = (): {
	cohort: LoomLandOptInCohort;
	isResolved: boolean;
	isEnrolled: boolean;
} => {
	const experiment = useExperiment(experiments.twcg2108LoomLandOptIn);
	const isEnrolled = experiment.type === EnrollmentType.ENROLLED;

	// Narrow to a single resolved-enrolled reference so reads of `value` are
	// type-safe (the not-enrolled / indeterminate arms of the union lack it).
	const resolvedExperiment =
		isEnrolled && experiment.usingDefault === false ? experiment : undefined;

	const isResolved = resolvedExperiment !== undefined;
	const cohort: LoomLandOptInCohort = resolvedExperiment?.value ?? 'control';

	return { cohort, isResolved, isEnrolled };
};
